import { useParams } from 'react-router-dom';
import { MAX_FILE_SIZE } from 'constants/index';
import { convertEnumsToReadableStrings } from 'utils/helpers';
import React, { useCallback, useEffect, useState } from 'react';
import { useAttachmentTypeQuery, usePurchaseRequestByIdQuery } from 'api';
import { CloseIcon, CloseIconLight, DoneIcon } from 'components/common/icons';
import { Button, Col, Form, Image, List, Row, Select, Typography, Upload, UploadContent } from 'components';
import styles from './../styles.module.scss';

const { Text } = Typography;
const { Dragger } = Upload;

interface IAttachmentForm {
  attachments: { type: string; file: File; id?: string }[];
  setAttachments: (attachments: { type: string; file: File }[]) => void;
  attachmentType?: string;
  setAttachmentType: (type: string | undefined) => void;
  fileList: File[];
  setFileList: (files: File[]) => void;
  onAdd?: (attachments: { type: string; file: File }[]) => void;
  onDelete?: (deletedIds: string[]) => void;
}

export const AttachmentForm = React.memo<IAttachmentForm>((props) => {
  const { attachments, setAttachments, onAdd, onDelete, setFileList, fileList, attachmentType, setAttachmentType } =
    props;
  const [disabledClear, setDisabledClear] = useState(true);
  const [disabledSave, setDisabledSave] = useState(true);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const { data: attachmentTypes = [] } = useAttachmentTypeQuery({ entityName: 'LEAD' });
  const { id } = useParams() as { id: string };
  const { data: { purchaseRequest } = {} } = usePurchaseRequestByIdQuery({ id }, { skip: true });

  const handleAdd = useCallback(() => {
    setAttachments([...(attachments as any), { type: attachmentType, file: fileList[0] }]);
    onAdd && onAdd([{ type: attachmentType as string, file: fileList[0] }]);
    setAttachmentType(undefined);
    setFileList([]);
  }, [attachments, attachmentType, fileList, onAdd, setAttachments, setAttachmentType, setFileList]);

  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setFileList([]);
      setError('File exceeds the maximum size of 5 MB.');
      return Upload.LIST_IGNORE;
    }

    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
        setIsPreviewVisible(true);
      };
      reader.readAsDataURL(file);
    }

    setFileList([file]);
    setError('');
    return false;
  };

  const handleDelete = (index: number) => {
    const newAttachments = [...attachments];
    const deleteAttachment = newAttachments.splice(index, 1);
    if (deleteAttachment[0].id) {
      onDelete && onDelete([deleteAttachment[0].id]);
    }
    setAttachments(newAttachments);
  };
  const handleCancel = () => {
    setAttachmentType(undefined);
    setFileList([]);
  };

  useEffect(() => {
    setDisabledSave(!(fileList?.length && attachmentType));
    setDisabledClear(!(fileList?.length || attachmentType));
  }, [fileList, attachmentType]);
  return (
    <>
      {attachments.length > 0 && (
        <List
          dataSource={attachments}
          style={{ marginBottom: 24 }}
          renderItem={(item, index) => (
            <List.Item
              className={styles.list_item}
              actions={[
                <Button
                  disabled={purchaseRequest?.isFinalStatus}
                  key={'add'}
                  size={'small'}
                  type={'link'}
                  icon={<CloseIconLight />}
                  onClick={() => handleDelete(index)}
                />,
              ]}
            >
              <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 400, height: 32, marginTop: 8, marginLeft: 12 }}>
                {item.type && <span className={styles.option_tag}>{convertEnumsToReadableStrings(item.type)}</span>}
                {item.file.name}
              </Text>
            </List.Item>
          )}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label={'Attachment'} name="file" validateStatus={error ? 'error' : ''} help={error || ''}>
            <Dragger
              disabled={purchaseRequest?.isFinalStatus}
              fileList={fileList as any}
              accept=".csv,.pdf,.png,.jpg,.jpeg"
              style={{ height: 40 }}
              beforeUpload={handleFileSizeValidation}
              onRemove={() => setFileList([])}
              maxCount={1}
            >
              <UploadContent title={'Upload File'} message={'Max size: 5MB / Format: CSV, PDF, PNG, JPG, JPEG'} />
            </Dragger>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={'Attachment Type'}>
            <Select
              disabled={purchaseRequest?.isFinalStatus}
              size={'large'}
              value={attachmentType}
              options={attachmentTypes}
              onChange={setAttachmentType}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Row gutter={4} justify={'end'}>
            <Col>
              <Button
                disabled={disabledClear}
                color="default"
                variant="filled"
                onClick={handleCancel}
                icon={<CloseIcon />}
              />
            </Col>
            <Col>
              <Button disabled={disabledSave} type="primary" icon={<DoneIcon />} onClick={handleAdd} />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Image Preview Modal (hidden image trigger) */}
      {previewImage && (
        <Image
          src={previewImage}
          style={{ display: 'none' }}
          preview={{
            visible: isPreviewVisible,
            onVisibleChange: (visible) => setIsPreviewVisible(visible),
          }}
        />
      )}
    </>
  );
});
